import React, { useState } from "react";
import { Table, Modal, Tooltip, Empty } from "antd";
import moment from "moment";
import { useHistory } from "react-router-dom";
import MediaFetcher from "../library/MediaFetcher";
import { useMediaQuery } from "../hooks/useMediaQuery";

// Helper function to get unique medias (moved outside component to avoid hooks issues)
const getUniqueMedias = (medias) => {
  return Array.from(
    new Map(medias.map(media => [media.key, media])).values()
  );
};

const MAX_VISIBLE = 4;

const AssignedAdGroupsTable = ({ assignedAdGroupUnit }) => {
  const history = useHistory();
  const [previewKey, setPreviewKey] = useState(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const navigateToGroup = (record) => {
    const firstMedia = record.medias?.[0];
    const mediaType = firstMedia?.type?.toUpperCase();
    const mediaTypePath = mediaType === 'VIDEO' ? 'video' : 'display';
    history.push(`/creatives/${mediaTypePath}/groups/${record.id}`);
  };

  const columns = [
    {
      title: "Ad Group Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (name, record) => {
        const label =
          name && name.length > 46 ? (
            <Tooltip title={name}>
              <span>{name.slice(0, 46).concat("...")}</span>
            </Tooltip>
          ) : (
            <span>{name}</span>
          );

        return (
          <span
            className="creative-table__name-link"
            onClick={() => {
              const firstMedia = record.medias?.[0];
              const mediaType = firstMedia?.type?.toUpperCase();
              const mediaTypePath = mediaType === 'VIDEO' ? 'video' : 'display';
              history.push(`/creatives/${mediaTypePath}/groups/${record.id}`);
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: "Ad Units",
      dataIndex: "AD Unit Number",
      key: "Ad Unit Number",
      width: "10%",
      align: "left",
      sorter: (a, b) => (a.medias?.length || 0) - (b.medias?.length || 0),
      render: (_, record) => record.medias.length,
    },
    {
      title: "Gallery",
      dataIndex: "medias",
      key: "medias",
      width: "25%",
      align: "left",
      render: medias => {
        const uniqueMedias = getUniqueMedias(medias);
        if (!uniqueMedias.length) return <div>No images</div>;

        const visible = uniqueMedias.slice(0, MAX_VISIBLE);
        const remaining = uniqueMedias.length - MAX_VISIBLE;

        return (
          <div
            className="creative-table__gallery"
            onClick={(e) => e.stopPropagation()}
          >
            {visible.map((m) => (
              <div
                key={m.id || m.key}
                className="creative-table__gallery-cell creative-table__gallery-cell--clickable"
                onClick={() => setPreviewKey(m.key)}
              >
                {m.key && (
                  <MediaFetcher
                    mediaKey={m.key}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                )}
              </div>
            ))}
            {remaining > 0 && (
              <div
                className="creative-table__gallery-cell creative-table__gallery-more"
                onClick={() => setPreviewKey(uniqueMedias[0].key)}
              >
                +{remaining}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "20%",
      align: "left",
      sorter: (a, b) =>
        moment(a.updatedAt).valueOf() - moment(b.updatedAt).valueOf(),
      render: d =>
        d ? moment(d).format("MM/DD/YYYY hh:mm A") : "—",
    },
  ];

  const renderMobileCards = () => {
    if (!assignedAdGroupUnit || assignedAdGroupUnit.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No assigned ad groups" />;
    }

    return (
      <div className="creative-adgroup-cards">
        {assignedAdGroupUnit.map((group) => {
          const uniqueMedias = getUniqueMedias(group.medias || []);
          const visible = uniqueMedias.slice(0, MAX_VISIBLE);
          const remaining = uniqueMedias.length - MAX_VISIBLE;

          return (
            <div
              key={group.id}
              className="creative-adgroup-card"
              onClick={() => navigateToGroup(group)}
            >
              <div className="creative-adgroup-card__header">
                <div className="creative-adgroup-card__name">{group.name}</div>
                <div className="creative-adgroup-card__count">
                  {group.medias?.length || 0} unit{(group.medias?.length || 0) !== 1 ? "s" : ""}
                </div>
              </div>
              {visible.length > 0 && (
                <div className="creative-adgroup-card__gallery">
                  {visible.map((m) => (
                    <div
                      key={m.id || m.key}
                      className="creative-adgroup-card__thumb"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewKey(m.key);
                      }}
                    >
                      {m.key && (
                        <MediaFetcher
                          mediaKey={m.key}
                          width="100%"
                          height="100%"
                          objectFit="cover"
                        />
                      )}
                    </div>
                  ))}
                  {remaining > 0 && (
                    <div className="creative-adgroup-card__thumb creative-adgroup-card__thumb--more">
                      +{remaining}
                    </div>
                  )}
                </div>
              )}
              <div className="creative-adgroup-card__footer">
                {group.updatedAt
                  ? moment(group.updatedAt).format("MMM DD, YYYY h:mm A")
                  : "\u2014"}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        renderMobileCards()
      ) : (
        <div className="creative-table">
          <Table
            size="middle"
            rowKey="id"
            pagination={false}
            scroll={{ x: 800 }}
            columns={columns}
            dataSource={assignedAdGroupUnit}
            bordered={false}
          />
        </div>
      )}
      <Modal
        open={!!previewKey}
        footer={null}
        closable={false}
        centered
        destroyOnClose
        onCancel={() => setPreviewKey(null)}
        className="creative-video-preview-modal creative-preview-modal--responsive"
        maskClosable
      >
        {previewKey && (
          <MediaFetcher
            mediaKey={previewKey}
            className="creative-detail-card__media-preview"
          />
        )}
      </Modal>
    </>
  );
};

export default AssignedAdGroupsTable;
